import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { QARunningWeightDialog } from "@/pages/QARunningWeightDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

// P17-UAT-15 REV-D1A · Running Weight = REGISTER-FIRST. "+ New Weight Entry"
// opens the transaction popup; row click opens the same popup (View/Edit/Authorise
// by lifecycle). No combined Entry+Spec+Register page.

const inp = "h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-sm focus:outline-none focus:border-[var(--accent)]";
const card = "border border-[var(--border)] rounded-md bg-[var(--bg-1)] p-4";
const fmt = (v, d = 3) => (v === null || v === undefined || v === "" ? "—" : Number(v).toFixed(d));
const STATUS_CLS = {
  DRAFT: "bg-zinc-500/15 text-zinc-300 border-zinc-500/40",
  CONFIRMED: "bg-sky-500/15 text-sky-300 border-sky-400/40",
  AUTHORISED: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
};

export default function QARunningWeight() {
  const { hasPerm } = useAuth();
  const canEdit = hasPerm("qa_running_weight:edit");
  const canAuthorise = hasPerm("qa_running_weight:approve");

  const [product, setProduct] = useState("");
  const [status, setStatus] = useState("");
  const [records, setRecords] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [dialogMode, setDialogMode] = useState("edit");

  const load = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (product) p.set("product_code", product);
      if (status) p.set("status", status);
      const { data } = await api.get(`/qa/running-weights?${p.toString()}`);
      setRecords(data.records || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load register"); }
  }, [product, status]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditingId(null); setDialogMode("edit"); setDialogOpen(true); };
  const openRow = (rid, m) => { setEditingId(rid); setDialogMode(m); setDialogOpen(true); };
  const confirmRow = async (rid) => {
    try { await api.post(`/qa/running-weights/${rid}/confirm`, {}); toast.success("Confirmed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Confirm failed"); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5" data-testid="qa-running-weight-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Running Weight</h1>
          <p className="text-sm text-zinc-500">Register · Product + Production-Date daily weight · DRAFT → CONFIRMED → AUTHORISED</p>
        </div>
        {canEdit && (
          <button data-testid="qa-new-weight-entry" onClick={openNew}
            className="h-9 px-4 rounded-sm bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold">+ New Weight Entry</button>
        )}
      </div>

      <div className={card}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase text-zinc-500">Product Code</label>
            <input className={inp} data-testid="qa-filter-product" value={product} onChange={(e) => setProduct(e.target.value)} placeholder="filter by product" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase text-zinc-500">Status</label>
            <select className={inp} data-testid="qa-filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All</option><option value="DRAFT">Draft</option>
              <option value="CONFIRMED">Confirmed</option><option value="AUTHORISED">Authorised</option>
            </select>
          </div>
          <button data-testid="qa-refresh" onClick={load} className="h-9 px-4 rounded-sm border border-[var(--border)] text-zinc-300 text-sm hover:bg-white/5">Refresh</button>
        </div>
      </div>

      <div className={card}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-[10px] uppercase text-zinc-500 border-b border-[var(--border)]">
              <th className="px-2 py-2 text-left">Product</th><th className="px-2 py-2 text-left">Prod. Date</th>
              <th className="px-2 py-2 text-right">Std g/m</th><th className="px-2 py-2 text-right">Actual g/m</th>
              <th className="px-2 py-2 text-right">Var %</th><th className="px-2 py-2 text-center">Tolerance</th>
              <th className="px-2 py-2 text-left">Confirmed By</th><th className="px-2 py-2 text-left">Authorised By</th>
              <th className="px-2 py-2 text-center">Status</th><th className="px-2 py-2 text-center">Action</th>
            </tr></thead>
            <tbody data-testid="qa-records-list">
              {records.map((r) => (
                <tr key={r.id} className="border-b border-[var(--border)]/40 hover:bg-white/5" data-testid={`qa-record-row-${r.id}`}>
                  <td className="px-2 py-1.5 font-mono text-zinc-200">{r.product_code}</td>
                  <td className="px-2 py-1.5 font-mono text-zinc-400">{r.production_date}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{fmt(r.standard_gpm, 3)}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-emerald-300">{fmt(r.actual_gpm, 3)}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{fmt(r.variance_pct, 2)}</td>
                  <td className={`px-2 py-1.5 text-center font-mono ${r.tolerance_pct != null ? "text-zinc-300" : "text-zinc-500"}`}>{r.tolerance_pct != null ? `±${fmt(r.tolerance_pct, 2)}%` : "—"}</td>
                  <td className="px-2 py-1.5 font-mono text-zinc-400">{r.confirmed_by || "—"}</td>
                  <td className="px-2 py-1.5 font-mono text-zinc-400">{r.authorized_by || "—"}</td>
                  <td className="px-2 py-1.5 text-center"><span className={`text-[9px] font-bold px-1 py-0.5 rounded-sm border uppercase ${STATUS_CLS[r.status] || ""}`}>{r.status}</span></td>
                  <td className="px-2 py-1.5 text-center">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid={`qa-actions-${r.id}`}
                          className="h-7 px-3 rounded-sm border border-[var(--border)] text-zinc-300 text-[11px] hover:bg-white/10">Actions ▾</button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[var(--bg-1)] border-[var(--border)] text-sm">
                        <DropdownMenuItem data-testid={`qa-action-view-${r.id}`} onClick={() => openRow(r.id, "view")}>View</DropdownMenuItem>
                        {r.status === "DRAFT" && canEdit && (
                          <DropdownMenuItem data-testid={`qa-action-edit-${r.id}`} onClick={() => openRow(r.id, "edit")}>Edit</DropdownMenuItem>
                        )}
                        {r.status === "DRAFT" && canEdit && (
                          <DropdownMenuItem data-testid={`qa-action-confirm-${r.id}`} onClick={() => confirmRow(r.id)}>Confirm</DropdownMenuItem>
                        )}
                        {r.status === "CONFIRMED" && canEdit && (
                          <DropdownMenuItem data-testid={`qa-action-edit-${r.id}`} onClick={() => openRow(r.id, "edit")}>Edit</DropdownMenuItem>
                        )}
                        {r.status === "CONFIRMED" && canAuthorise && (
                          <DropdownMenuItem data-testid={`qa-action-authorise-${r.id}`} onClick={() => openRow(r.id, "view")}>Authorise</DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
              {records.length === 0 && <tr><td colSpan={10} className="px-2 py-6 text-center text-zinc-600">No records — use “+ New Weight Entry”.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <QARunningWeightDialog open={dialogOpen} recordId={editingId} mode={dialogMode}
        onClose={() => setDialogOpen(false)} onSaved={load} />
    </div>
  );
}
